import React, { useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TablePagination from "@mui/material/TablePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExpand,
  faMinimize,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./../../styles/TextAnalysisTable.css";
import { Tooltip } from "@mui/material";
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1AACAC",
    color: theme.palette.common.white,
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    padding: theme.spacing(1.2),
    lineHeight: "1.2rem",
    border: "none", // Reset all borders
    borderRight: `1px solid ${theme.palette.divider}`, // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    backgroundColor: theme.palette.common.white,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    padding: theme.spacing(1.2),
    border: "none", // Reset all borders
    borderRight: `1px solid ${theme.palette.divider}`, // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "rgba(249, 84, 84, 0.04)",
    transition: "background-color 0.2s ease",
  },
}));

function TextAnalysisTableNews({ analysisData = [], tableRef }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tableContainerRef = useRef();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      tableContainerRef.current.requestFullscreen();
      setIsFullscreen(isFullscreen);
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = Array.isArray(analysisData)
    ? analysisData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const formatDate = (datetime) => {
    if (!datetime) return "N/A";
    const date = new Date(datetime);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const downloadPdf = () => {
    const input = document.getElementById("tableToDownload");
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");
      const imgWidth = 297;
      const pageHeight = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("table.pdf");
    });
  };
  //
  return (
    <Box
      sx={{
        width: isFullscreen ? "100vw" : "auto",
        height: isFullscreen ? "100vh" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
        borderRadius: "15px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)", // Added box shadow
        // marginTop: "30px",
      }}
    >
      <TableContainer
        component={Paper}
        className="custom-table-container"
        ref={tableContainerRef}
      >
        <Table
          stickyHeader
          sx={{ minWidth: 800, tableLayout: "fixed" }}
          aria-label="customized table"
          ref={tableRef}
          id="tableToDownload"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: 25 }}>S.No</StyledTableCell>
              <StyledTableCell sx={{ width: 55 }}>Date</StyledTableCell>
              <StyledTableCell sx={{ width: 70 }}>News Channel</StyledTableCell>
              <StyledTableCell sx={{ width: 200 }}>Title</StyledTableCell>
              <StyledTableCell sx={{ width: 80 }}>Author</StyledTableCell>
              <StyledTableCell sx={{ width: 80 }}>
                Sentiment Accuracy
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 &&
              paginatedData.map((data, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>
                    {page * rowsPerPage + index + 1}
                  </StyledTableCell>
                  <StyledTableCell>
                    {new Intl.DateTimeFormat("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "2-digit",
                      timeZone: "UTC", // avoids local timezone conversion
                    }).format(new Date(data.datePublished))}
                    , 
                    {new Intl.DateTimeFormat("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                      timeZone: "UTC", // avoids local timezone conversion
                    }).format(new Date(data.datePublished))}
                  </StyledTableCell>
                  <StyledTableCell>{data.newsChannel || "N/A"}</StyledTableCell>
                  <StyledTableCell>{data.title || "N/A"}</StyledTableCell>
                  <StyledTableCell>{data.author || "N/A"}</StyledTableCell>
                  <StyledTableCell>
                    Positive: {data.sentimentAccuracy?.positive || "N/A"}%,
                    <br />
                    Negative: {data.sentimentAccuracy?.negative || "N/A"}%
                  </StyledTableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          outline: "none",
          backgroundColor: "white",
        }}
      >
        <Tooltip title="Expand">
          <Button onClick={toggleFullscreen}>
            {isFullscreen ? (
              <FontAwesomeIcon icon={faMinimize} fontSize={"15px"} />
            ) : (
              <FontAwesomeIcon icon={faExpand} fontSize={"20px"} />
            )}
          </Button>
        </Tooltip>
        {/* <Button onClick={downloadPdf}>
          <FontAwesomeIcon icon={faFilePdf} fontSize={"20px"} />
        </Button> */}
        <TablePagination
          component="div"
          count={Array.isArray(analysisData) ? analysisData.length : 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ minWidth: "auto" }} // Adjust the size of pagination box
        />
      </Box>
    </Box>
  );
}

export default TextAnalysisTableNews;
